import {
  ControlAttributesMetadata,
  FilterOption,
  GetControlAttributesCommandInput,
  AttributeMetadata,
} from '@amzn/controllershiphub-typescript-client';
import { useControlAttributes } from './useControlData';
import { useState, useMemo, useEffect } from 'react';

export interface UseControlMetadataResult {
  metadata: ControlAttributesMetadata | null;
  formattedFilters: Record<string, string[]>;
  defaultOptions: Record<string, FilterOption>;
  hasFilters: boolean;
  defaultColumnState: any[];
  isLoading: boolean;
  error: string | null;
  setError: (error: string | null) => void;
}

export function useControlMetadata(controlId: string | undefined): UseControlMetadataResult {
  const { data, isLoading, error } = useControlAttributes({ controlId });
  const [customError, setCustomError] = useState<string | null>(null);

  const metadata = data?.controlAttributesMetadata || null;

  useEffect(() => {
    if (error) {
      setCustomError(error.message);
    }
  }, [error]);

  const { formattedFilters, defaultOptions, hasFilters, defaultColumnState } = useMemo(() => {
    let formattedFilters: Record<string, string[]> = {};
    let defaultOptions: Record<string, FilterOption> = {};
    let hasFilters = false;
    let defaultColumnState: any[] = [];

    if (metadata?.defaultFilters) {
      try {
        const parsedDefaultFilters = JSON.parse(metadata.defaultFilters);
        console.log('Raw defaultFilters from API:', parsedDefaultFilters);
        const defaultFilters = parsedDefaultFilters.filters || {};
        defaultColumnState = parsedDefaultFilters.columnState || [];
        console.log('Extracted defaultColumnState:', defaultColumnState);

        Object.entries(defaultFilters).forEach(([key, value]) => {
          if (value && typeof value === 'object' && 'filterModels' in value) {
            const filters = (value.filterModels as any[])
              .map((model) => model.filter)
              .filter(Boolean);
            if (filters.length > 0) {
              hasFilters = true;
              formattedFilters[key] = filters;
              defaultOptions[key] = { type: 'string', isMultiSelect: true };
            }
          }
        });
      } catch (err) {
        console.error('Error parsing default filters:', err);
      }
    }

    return { formattedFilters, defaultOptions, hasFilters, defaultColumnState };
  }, [metadata]);

  return {
    metadata,
    formattedFilters,
    defaultOptions,
    hasFilters,
    defaultColumnState,
    isLoading,
    error: customError,
    setError: setCustomError,
  };
}
